/** The game's tooltip (docs/ui-decisions.md): styled like the UI, shown
 *  quickly next to what it explains, kept on screen. Elements opt in with
 *  `data-tip` (fixed text) or `setTip` (text worked out when shown). */

import { $ } from "./format";

const dynamic = new WeakMap<Element, () => string>();
const DELAY_MS = 250;

/** A tooltip whose text is computed each time it shows (live figures). */
export function setTip(el: Element, text: () => string): void {
  dynamic.set(el, text);
  (el as HTMLElement).dataset.tip = "";
}

export function initTips(): void {
  const tip = $("ui-tip");
  let timer = 0;
  let over: HTMLElement | null = null;
  const hide = () => {
    clearTimeout(timer);
    over = null;
    tip.classList.add("hidden");
  };
  const show = (el: HTMLElement) => {
    const text = dynamic.get(el)?.() ?? el.dataset.tip ?? "";
    if (!text) return;
    tip.textContent = text;
    tip.classList.remove("hidden");
    // Below the element, or above when there's no room; inside the window.
    // One redrawn away (detached) has no box: the pointer stands in.
    const r = el.isConnected ? el.getBoundingClientRect() : new DOMRect(pointer[0] - 8, pointer[1] - 8, 16, 16);
    const t = tip.getBoundingClientRect();
    const below = r.bottom + 6 + t.height <= window.innerHeight;
    const top = below ? r.bottom + 6 : r.top - 6 - t.height;
    const left = Math.min(Math.max(4, r.left + r.width / 2 - t.width / 2), window.innerWidth - t.width - 4);
    tip.style.top = `${Math.max(4, top)}px`;
    tip.style.left = `${left}px`;
  };
  let pointer: [number, number] = [0, 0];
  document.addEventListener("pointermove", (e) => (pointer = [e.clientX, e.clientY]), { passive: true });
  document.addEventListener("pointerover", (e) => {
    const el = (e.target as HTMLElement).closest<HTMLElement>("[data-tip]");
    if (el === over) return;
    // Redrawn in place (the map's overlay rebuilds as it moves): the same
    // tip carries over to the new element, delay and all.
    if (el && over && !dynamic.has(el) && el.dataset.tip && el.dataset.tip === over.dataset.tip) {
      over = el;
      return;
    }
    hide();
    if (!el) return;
    over = el;
    timer = window.setTimeout(() => over && show(over), DELAY_MS);
  });
  document.addEventListener("pointerdown", hide, true);
  document.addEventListener("keydown", hide, true);
  window.addEventListener("blur", hide);
  // Live figures: refresh while shown.
  window.setInterval(() => {
    if (over && !tip.classList.contains("hidden") && dynamic.has(over)) show(over);
  }, 500);
}
